<template>
    <div class="main">
        <h1>Medlemmer</h1>
        <p>{{ memberCount }} {{ memberCount === 1 ? 'medlem' : 'medlemmer' }} i vaskerummet.</p>

        <!-- Auto-remove inactive users settings -->
        <div class="auto-remove-section">
            <div class="auto-remove-toggle">
                <input
                    type="checkbox"
                    id="autoRemoveToggle"
                    :checked="autoRemoveEnabled"
                    @change="autoRemoveEnabled = $event.target.checked"
                />
                <label>Fjern automatisk inaktive brugere</label>
            </div>
            <div v-if="autoRemoveEnabled" class="auto-remove-months">
                <span>Fjern efter</span>
                <input
                    v-model="inactiveMonthsThreshold"
                    @keypress="isNumber($event)"
                    maxlength="2"
                />
                <span>{{ inactiveMonthsThreshold == 1 ? 'måned' : 'måneder' }} uden aktivitet</span>
            </div>
            <div v-if="autoRemoveEnabled && !isSavingAutoRemove && String(inactiveMonthsThreshold) !== String(savedInactiveMonthsThreshold)" class="auto-remove-save">
                <primary-button :disabled="isSavingAutoRemove" @click="handleSaveAutoRemove">
                    {{ isSavingAutoRemove ? 'Gemmer...' : 'Gem' }}
                </primary-button>
            </div>
        </div>

        <!-- Confirm remove modal -->
        <div v-if="confirmRemoveUser" class="modal">
            <div class="modal-content">
                <span @click="handleCancelRemove" class="close">&times;</span>
                <div class="heading">Fjern medlem</div>
                <p class="confirm-text">Er du sikker på, at du vil fjerne <strong>{{ confirmRemoveUser.name }}</strong>?</p>
                <p class="confirm-subtext">Brugerens fremtidige bookinger vil også blive slettet.</p>
                <div class="modal-button-container">
                    <secondary-button :disabled="isLoading" @click="handleCancelRemove">Annuller</secondary-button>
                    <primary-button :disabled="isLoading" @click="handleConfirmRemove" class="margin-left remove-btn">
                        {{ isLoading ? "Fjerner..." : "Fjern" }}
                    </primary-button>
                </div>
            </div>
        </div>

        <!-- Member list -->
        <div class="member-list">
            <div v-if="members.length === 0" class="empty-state">Ingen medlemmer fundet.</div>
            <div v-for="member in members" :key="member.uid" class="member-row">
                <div class="member-info">
                    <div class="member-name">{{ member.name || 'Ingen navn' }}</div>
                    <div class="member-email">{{ member.email }}</div>
                </div>
                <button
                    v-if="member.uid !== adminUid"
                    class="remove-icon-btn"
                    @click="handleRemove(member)"
                    title="Fjern medlem"
                >
                    <font-awesome-icon icon="trash-can" />
                </button>
                <span v-else class="admin-badge">Admin</span>
            </div>
        </div>

        <div class="button-container back-btn-container">
            <secondary-button @click="$emit('cancel')">Tilbage</secondary-button>
        </div>
    </div>
</template>

<script setup>
import PrimaryButton from './PrimaryButton.vue';
import SecondaryButton from './SecondaryButton.vue';
import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import { getDatabase, ref as firebaseRef, update } from 'firebase/database';

const store = useStore();
const toast = useToast();
const emit = defineEmits(['cancel']);

const isLoading = ref(false);
const confirmRemoveUser = ref(null);
const isSavingAutoRemove = ref(false);

const adminUid = computed(() => store.state.roomSettings?.admin);

// Auto-remove settings
const autoRemoveEnabled = ref(store.state.roomSettings?.autoRemoveInactive || false);
const inactiveMonthsThreshold = ref(store.state.roomSettings?.inactiveMonthsThreshold || 6);
const savedAutoRemoveEnabled = ref(store.state.roomSettings?.autoRemoveInactive || false);
const savedInactiveMonthsThreshold = ref(store.state.roomSettings?.inactiveMonthsThreshold || 6);

const isNumber = (event) => {
    var charCode = event.keyCode || event.which;
    if (charCode < 48 || charCode > 57) event.preventDefault();
};

const autoRemoveSettingsChanged = computed(() => {
    if (autoRemoveEnabled.value !== savedAutoRemoveEnabled.value) return true;
    if (autoRemoveEnabled.value &&
        String(inactiveMonthsThreshold.value) !== String(savedInactiveMonthsThreshold.value)) return true;
    return false;
});

// Auto-save when checking or unchecking the toggle
watch(autoRemoveEnabled, async (newVal, oldVal) => {
    if (oldVal !== undefined && newVal !== oldVal) {
        isSavingAutoRemove.value = true;
        await handleSaveAutoRemove();
    }
});

const handleSaveAutoRemove = async () => {
    const months = parseInt(inactiveMonthsThreshold.value);
    if (autoRemoveEnabled.value && (!months || months < 1)) {
        toast.error('Angiv venligst et gyldigt antal måneder (mindst 1)');
        isSavingAutoRemove.value = false;
        return;
    }

    if (!isSavingAutoRemove.value) {
        isSavingAutoRemove.value = true;
    }

    try {
        const db = getDatabase();
        const laundryRoomId = store.state.userDBInfo.laundryroom;

        await update(
            firebaseRef(db, `laundryrooms/${laundryRoomId}/settings/`),
            {
                autoRemoveInactive: autoRemoveEnabled.value,
                inactiveMonthsThreshold: autoRemoveEnabled.value ? months : null,
            }
        );

        savedAutoRemoveEnabled.value = autoRemoveEnabled.value;
        savedInactiveMonthsThreshold.value = autoRemoveEnabled.value ? months : '';

        toast.success('Indstillinger for automatisk fjernelse er gemt');
    } catch (error) {
        console.error('Error saving auto-remove settings:', error);
        toast.error('Fejl ved gemning af indstillinger');
    }

    isSavingAutoRemove.value = false;
};

const members = computed(() => {
    const membersObj = store.state.roomMembers;
    if (!membersObj) return [];
    return Object.entries(membersObj)
        .map(([uid, data]) => ({ uid, ...data }))
        .sort((a, b) => {
            // Admin first, then alphabetical by name
            if (a.uid === adminUid.value) return -1;
            if (b.uid === adminUid.value) return 1;
            return (a.name || '').localeCompare(b.name || '');
        });
});

const memberCount = computed(() => members.value.length);

const handleRemove = (member) => {
    confirmRemoveUser.value = member;
};

const handleCancelRemove = () => {
    confirmRemoveUser.value = null;
};

const handleConfirmRemove = async () => {
    isLoading.value = true;
    try {
        const roomId = store.state.userDBInfo.laundryroom;
        await store.dispatch('callRemoveUserFromRoom', {
            userId: confirmRemoveUser.value.uid,
            roomId,
        });
        toast.success(`${confirmRemoveUser.value.name || 'Bruger'} er blevet fjernet`);
        confirmRemoveUser.value = null;
    } catch (error) {
        console.error('Error removing user:', error);
        toast.error('Fejl ved fjernelse af bruger');
    }
    isLoading.value = false;
};
</script>

<style scoped>
.main {
    border-radius: 4px;
    width: 80%;
    height: 100vh;
    margin: auto;
    display: flex;
    justify-content: center;
    flex-direction: column;
}

h1 {
    font-size: 1.2rem;
    font-weight: 600;
    text-align: center;
    margin-bottom: 0px;
}

p {
    margin-top: 24px;
    margin-bottom: 24px;
    font-size: 1rem;
    text-align: center;
}

.auto-remove-section {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e5e7eb;
}

.auto-remove-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
}

.auto-remove-toggle input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin: 0;
    flex-shrink: 0;
}

.auto-remove-toggle label {
    color: #545454;
    font-weight: 600;
    font-size: 0.95rem;
    line-height: 20px;
    margin: 0;
    padding: 0;
}

.auto-remove-months {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    font-size: 0.9rem;
    color: #545454;
}

.auto-remove-months input {
    width: 60px;
    padding: 4px 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    color: rgba(0, 0, 0, 0.8);
    text-align: center;
}

.auto-remove-save {
    margin-top: 12px;
}

.member-list {
    max-height: 50vh;
    overflow-y: auto;
    margin-bottom: 24px;
}

.member-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #e5e7eb;
}

.member-row:last-child {
    border-bottom: none;
}

.member-info {
    flex: 1;
    min-width: 0;
}

.member-name {
    font-weight: 600;
    font-size: 0.95rem;
    color: #545454;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.member-email {
    font-size: 0.85rem;
    color: #838383;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.remove-icon-btn {
    background: none;
    border: none;
    color: #838383;
    cursor: pointer;
    padding: 8px;
    font-size: 1rem;
    flex-shrink: 0;
}

.remove-icon-btn:hover {
    color: #ff5a5f;
}

.admin-badge {
    font-size: 0.8rem;
    color: #00bae0;
    font-weight: 600;
    flex-shrink: 0;
}

.empty-state {
    text-align: center;
    color: #838383;
    padding: 24px 0;
}

.modal {
    text-align: center;
    display: block;
    position: fixed;
    z-index: 30;
    left: 0;
    right: 0;
    top: 0;
    margin: auto;
    display: flex;
    width: 100%;
    height: 100%;
    max-width: 450px;
    overflow: auto;
    background-color: rgb(0, 0, 0);
    background-color: rgba(0, 0, 0, 0.3);
}

.modal-content {
    position: relative;
    border-radius: 4px;
    background-color: #fefefe;
    margin: auto;
    padding: 36px 20px;
    width: 80%;
    max-width: 400px;
    min-width: 300px;
}

.heading {
    font-size: 1.7rem;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.8);
    margin-bottom: 5px;
}

.close {
    top: 0px;
    right: 12px;
    position: absolute;
    color: #aaaaaa;
    font-size: 35px;
    cursor: pointer;
    font-weight: bold;
}

.close:hover,
.close:focus {
    color: #000;
    text-decoration: none;
}

.confirm-text {
    font-size: 1rem;
    margin-bottom: 4px;
}

.confirm-subtext {
    font-size: 0.85rem;
    color: #838383;
    margin-top: 0;
}

.modal-button-container {
    display: flex;
    margin-top: 20px;
}

.button-container {
    display: flex;
}

.margin-left {
    margin-left: 4px;
}

.remove-btn {
    background-color: #ff5a5f;
}

.back-btn-container {
    margin-top: 0;
}

@media screen and (min-width: 455px) {
    .modal {
        top: 15px;
        height: calc(100% - 30px);
        border-radius: 4px;
    }
}
</style>
